import { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  GraduationCap,
  Shield,
  Eye,
  Zap,
  Bot,
  CheckCircle2,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

interface Stage {
  label: string;
  shortLabel: string;
  description: string;
  confidence: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  detail: string;
}

const stages: Stage[] = [
  {
    label: 'MANUAL',
    shortLabel: 'Manual',
    description: 'Every action requires human approval',
    confidence: 0,
    icon: <Shield className="w-5 h-5" />,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    detail: 'Bot executes nothing without explicit human sign-off. Full audit trail of every decision.',
  },
  {
    label: 'SEMI_AUTO',
    shortLabel: 'Semi-Auto',
    description: 'Low-risk actions execute freely',
    confidence: 50,
    icon: <Eye className="w-5 h-5" />,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    detail: 'After consistent approvals, bot earns trust on simple tasks. Complex actions still require review.',
  },
  {
    label: 'AUTO_WITH_REVIEW',
    shortLabel: 'Auto + Review',
    description: 'Most actions autonomous, sampled for audit',
    confidence: 80,
    icon: <Zap className="w-5 h-5" />,
    color: 'text-teal-400',
    bgColor: 'bg-teal-500/10',
    borderColor: 'border-teal-500/30',
    detail: 'Bot operates independently with periodic random sampling. Audit team reviews 5-10% of actions.',
  },
  {
    label: 'FULL_AUTO',
    shortLabel: 'Full Auto',
    description: 'Trusted team member. Full autonomy.',
    confidence: 95,
    icon: <GraduationCap className="w-5 h-5" />,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    detail: 'Bot is a permanent, trusted team member. Can train newer bots. Only escalates true edge cases.',
  },
];

const sampleApprovals = [
  { action: 'Drafted email to investor', result: 'approved' as const },
  { action: 'Scored lead at 87%', result: 'approved' as const },
  { action: 'Scheduled interview with candidate', result: 'approved' as const },
  { action: 'Updated CRM with call notes', result: 'approved' as const },
  { action: 'Sent follow-up to prospect', result: 'approved' as const },
  { action: 'Flagged unusual transaction', result: 'rejected' as const },
  { action: 'Drafted quarterly update', result: 'approved' as const },
  { action: 'Recommended pricing change', result: 'approved' as const },
];

function ConfidenceBar({ target, start }: { target: number; start: boolean }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!start) return;
    const timeout = setTimeout(() => setWidth(target), 100);
    return () => clearTimeout(timeout);
  }, [start, target]);

  return (
    <div className="w-full h-2 bg-slate-200 dark:bg-slate-700/50 rounded-full overflow-hidden">
      <motion.div
        className="h-full rounded-full"
        style={{
          background: `linear-gradient(90deg, #ef4444 0%, #f59e0b 40%, #0AB3BF 70%, #10b981 100%)`,
        }}
        initial={{ width: 0 }}
        animate={{ width: `${width}%` }}
        transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
      />
    </div>
  );
}

export const BotGraduationTimeline = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [activeStage, setActiveStage] = useState(0);
  const [demoIndex, setDemoIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-advance the demo stage and feed while in view.
  // Pause on hover so users can read a stage without fighting the carousel.
  useEffect(() => {
    if (!isInView || isPaused) return;

    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % stages.length);
      setDemoIndex((prev) => (prev + 1) % sampleApprovals.length);
    }, 3500);

    return () => clearInterval(interval);
  }, [isInView, isPaused]);

  const currentApproval = sampleApprovals[demoIndex];

  return (
    <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="text-center mb-16"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-400 rounded-full text-sm font-medium border dark:border-teal-500/20 mb-6">
          <GraduationCap className="w-4 h-4" />
          Bots That Earn Their Place
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
          Trust Through{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-500 dark:from-teal-400 dark:to-emerald-400">
            Proven Performance
          </span>
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          Every bot starts on a leash. Consistent approvals unlock more freedom. No bot graduates without earning it.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: Stages */}
        <motion.div
          className="space-y-3"
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {stages.map((stage, idx) => {
            const isActive = idx === activeStage;
            const isPast = idx < activeStage;
            return (
              <motion.button
                key={stage.label}
                onClick={() => setActiveStage(idx)}
                className={`w-full text-left rounded-xl border transition-all duration-300 overflow-hidden
                  ${
                    isActive
                      ? `${stage.borderColor} ${stage.bgColor} shadow-lg`
                      : isPast
                      ? 'border-emerald-200 dark:border-emerald-500/20 bg-emerald-50/60 dark:bg-emerald-500/5'
                      : 'border-slate-200 dark:border-slate-700/30 bg-white dark:bg-slate-800/30 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="p-4 flex items-center gap-4">
                  {/* Stage number / check */}
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors
                      ${
                        isActive
                          ? `${stage.bgColor} ${stage.color}`
                          : isPast
                          ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'
                          : 'bg-slate-100 text-slate-400 dark:bg-slate-700/50 dark:text-slate-500'
                      }`}
                  >
                    {isPast ? <CheckCircle2 className="w-5 h-5" /> : stage.icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${isActive ? stage.color : isPast ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`}>
                        {stage.shortLabel}
                      </span>
                      {isActive && (
                        <motion.span
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          className="text-label-2xs px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-400 font-medium"
                        >
                          Current
                        </motion.span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{stage.description}</div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className={`text-lg font-bold ${isActive ? stage.color : isPast ? 'text-emerald-400' : 'text-slate-600'}`}>
                      {stage.confidence}%
                    </div>
                    <div className="text-label-2xs text-slate-600">confidence</div>
                  </div>
                </div>

                {/* Detail panel — fixed height so auto-cycling never reflows the page */}
                <div className="h-[72px] px-4 pb-4 pt-1 relative">
                  <AnimatePresence mode="wait">
                    {isActive ? (
                      <motion.div
                        key={stage.label}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.25 }}
                        className="absolute inset-x-4 top-1 bottom-4"
                      >
                        <ConfidenceBar target={stage.confidence} start={isActive} />
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 line-clamp-2">{stage.detail}</p>
                      </motion.div>
                    ) : (
                      <motion.div
                        key={`${stage.label}-placeholder`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-x-4 top-1 bottom-4"
                      >
                        <div className="w-full h-2 bg-slate-200 dark:bg-slate-700/20 rounded-full" />
                        <div className="mt-2 h-3 w-3/4 bg-slate-200 dark:bg-slate-700/20 rounded" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.button>
            );
          })}
        </motion.div>

        {/* Right: Live Demo */}
        <motion.div
          className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-xl p-6 backdrop-blur-sm flex flex-col"
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Bot className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Live Approval Feed</h3>
            <div className="ml-auto flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-emerald-400">Simulated</span>
            </div>
          </div>

          {/* Bot Card */}
          <div className="bg-white dark:bg-slate-800/60 rounded-lg p-4 mb-4 border border-slate-200 dark:border-slate-700/30">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">AI Fundraiser Bot</div>
                <div className="text-xs text-slate-500">ID: bot_fund_001</div>
              </div>
              <div className="ml-auto">
                <span
                  className={`text-xs px-2 py-1 rounded-full font-medium ${stages[activeStage].bgColor} ${stages[activeStage].color} border ${stages[activeStage].borderColor}`}
                >
                  {stages[activeStage].shortLabel}
                </span>
              </div>
            </div>

            <ConfidenceBar target={stages[activeStage].confidence} start={isInView} />
            <div className="flex justify-between text-label-2xs text-slate-500 mt-1">
              <span>0%</span>
              <span>Confidence Threshold</span>
              <span>100%</span>
            </div>
          </div>

          {/* Action Feed */}
          <div className="flex-1 space-y-2 overflow-hidden">
            <AnimatePresence mode="popLayout">
              {sampleApprovals.slice(demoIndex, demoIndex + 4).map((item, i) => (
                <motion.div
                  key={`${demoIndex}-${i}`}
                  initial={{ opacity: 0, x: 20, height: 0 }}
                  animate={{ opacity: 1, x: 0, height: 'auto' }}
                  exit={{ opacity: 0, x: -20, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex items-center gap-3 p-3 rounded-lg border text-sm
                    ${
                      i === 0
                        ? 'bg-teal-50 dark:bg-slate-800/80 border-teal-200 dark:border-teal-500/30'
                        : 'bg-white dark:bg-slate-800/30 border-slate-200 dark:border-slate-700/20'
                    }`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
                      ${item.result === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}
                  >
                    {item.result === 'approved' ? (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                      <Shield className="w-3.5 h-3.5" />
                    )}
                  </div>
                  <span className="text-slate-600 dark:text-slate-300 flex-1 truncate">{item.action}</span>
                  <span
                    className={`text-xs font-medium flex-shrink-0
                      ${item.result === 'approved' ? 'text-emerald-400' : 'text-red-400'}`}
                  >
                    {item.result === 'approved' ? 'Approved' : 'Review'}
                  </span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Result indicator */}
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700/30">
            <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
              <Sparkles className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
              <span>
                {activeStage < stages.length - 1
                  ? `${sampleApprovals.filter((a) => a.result === 'approved').length}/${sampleApprovals.length} approvals → next stage unlocks at 90%`
                  : 'Bot is a fully trusted team member. Can now mentor new bots.'}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom: Timeline steps */}
      <motion.div
        className="mt-12 flex items-center justify-center"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-slate-500">
          <span className="text-slate-600 dark:text-slate-400 font-medium">Typical graduation:</span>
          <span>Week 1-2</span>
          <ArrowRight className="w-3 h-3" />
          <span>Week 3-4</span>
          <ArrowRight className="w-3 h-3" />
          <span>Month 2-3</span>
          <ArrowRight className="w-3 h-3" />
          <span className="text-emerald-400 font-medium">Month 4+</span>
        </div>
      </motion.div>
    </div>
  );
};
